// apps/client/src/pages/ProfilePage.tsx
//
// User Profile & Settings — V2 Beelucky Sweepstakes
// Shows avatar, stats, settings toggles, TON wallet connection
//

import { useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { useAuthStore } from '../store/authStore';
import { useUserStore } from '../store/userStore';
import { useTranslation } from 'react-i18next';
import Icon from '../ui-kit/Icon';
import { BottomNav } from '../ui-kit/Layouts';
import { api } from '../lib/api';

type ViewType = 'home' | 'game' | 'missions' | 'store' | 'referrals' | 'sunday' | 'profile';

interface ProfilePageProps {
    onNavigate: (view: ViewType) => void;
}

export function ProfilePage({ onNavigate }: ProfilePageProps) {
    const { user } = useAuthStore();
    const { chips, settings, updateSettings, userStats } = useUserStore();
    const [tonConnectUI] = useTonConnectUI();
    const wallet = useTonWallet();
    const { t } = useTranslation();

    // Save wallet address to backend when connected
    const saveWalletToBackend = useCallback(async (address: string) => {
        try {
            await api.updateWallet(address);
            console.log('💎 Wallet saved to backend:', address);
        } catch (error) {
            console.error('Failed to save wallet:', error);
        }
    }, []);

    // Watch for wallet connection changes
    useEffect(() => {
        if (wallet?.account?.address) {
            saveWalletToBackend(wallet.account.address);
        }
    }, [wallet?.account?.address, saveWalletToBackend]);

    const handleConnectWallet = async () => {
        try {
            await tonConnectUI.openModal();
        } catch (error) {
            console.error('Failed to open wallet modal:', error);
        }
    };

    const handleDisconnectWallet = async () => {
        try {
            await tonConnectUI.disconnect();
            // Clear wallet from backend
            await api.updateWallet('');
            console.log('💎 Wallet disconnected');
        } catch (error) {
            console.error('Failed to disconnect wallet:', error);
        }
    };

    // Truncate address for display: EQAB...1234
    const truncateAddress = (addr: string) =>
        `${addr.slice(0, 6)}...${addr.slice(-4)}`;

    const handleNavAction = (tab: string) => {
        const validViews = ['game', 'store', 'sunday', 'home', 'missions', 'referrals', 'profile'] as const;
        type ValidView = typeof validViews[number];
        if (tab === 'profile') return; // already here
        if (validViews.includes(tab as ValidView)) {
            onNavigate(tab as ValidView);
        } else {
            onNavigate('home');
        }
    };

    return (
        <div className="min-h-screen bg-purple-gradient">
            {/* Header */}
            <header className="px-4 pt-6 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => onNavigate('home')}
                        className="w-10 h-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center"
                    >
                        <Icon name="arrow_back" className="text-white" />
                    </motion.button>
                    <h1 className="text-lg font-bold text-white font-display">{t('profile.title')}</h1>
                </div>
            </header>

            <main className="pt-4 pb-32 px-4 max-w-lg mx-auto space-y-6">
                {/* Profile card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-panel border-white/10 rounded-3xl p-6 text-center"
                >
                    {/* Avatar */}
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mx-auto mb-4 border-2 border-primary/30 shadow-[0_0_20px_rgba(168,85,247,0.3)]">
                        {user?.photoUrl ? (
                            <img
                                src={user.photoUrl}
                                alt={user.firstName}
                                className="w-full h-full rounded-full object-cover"
                            />
                        ) : (
                            <span className="text-3xl font-black text-white">
                                {user?.firstName?.charAt(0) || '?'}
                            </span>
                        )}
                    </div>

                    <h2 className="text-xl font-bold text-white">{user?.firstName || 'User'}</h2>
                    <p className="text-white/40 text-sm">@{user?.username || 'bingoton_user'}</p>

                    {/* Chip balance */}
                    <div className="mt-4 inline-flex items-center gap-2 bg-[#FBBF24]/10 px-4 py-2 rounded-pill">
                        <Icon name="toll" className="text-lg text-[#FBBF24]" />
                        <span className="text-lg font-bold text-[#FBBF24]">{chips.toLocaleString()}</span>
                        <span className="text-[#FBBF24]/50 text-xs font-bold">BingoChips</span>
                    </div>
                </motion.div>

                {/* Stats card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="glass-panel border-white/10 rounded-3xl p-6"
                >
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <Icon name="leaderboard" className="text-primary" />
                        {t('profile.stats')}
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-black text-white">{userStats.gamesPlayed}</div>
                            <div className="text-white/40 text-[10px] font-bold uppercase">{t('profile.games')}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-black text-[#FFC107]">{userStats.bingosWon}</div>
                            <div className="text-white/40 text-[10px] font-bold uppercase">{t('profile.bingos')}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-black text-green-400">{userStats.totalEarned}</div>
                            <div className="text-white/40 text-[10px] font-bold uppercase">{t('profile.earned')}</div>
                        </div>
                    </div>
                </motion.div>

                {/* Settings */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="glass-panel border-white/10 rounded-3xl p-6"
                >
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <Icon name="settings" className="text-primary" />
                        {t('profile.settings')}
                    </h3>
                    <div className="space-y-4">
                        {/* Sound toggle */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-lg">🔊</span>
                                <span className="text-white text-sm font-bold">{t('profile.sound')}</span>
                            </div>
                            <button
                                onClick={() => updateSettings({ sound: !settings.sound })}
                                className={`w-12 h-7 rounded-full transition-all relative ${settings.sound ? 'bg-primary' : 'bg-white/20'
                                    }`}
                            >
                                <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${settings.sound ? 'left-6' : 'left-1'
                                    }`} />
                            </button>
                        </div>

                        {/* Vibration toggle */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-lg">📳</span>
                                <span className="text-white text-sm font-bold">{t('profile.vibration')}</span>
                            </div>
                            <button
                                onClick={() => updateSettings({ vibration: !settings.vibration })}
                                className={`w-12 h-7 rounded-full transition-all relative ${settings.vibration ? 'bg-primary' : 'bg-white/20'
                                    }`}
                            >
                                <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${settings.vibration ? 'left-6' : 'left-1'
                                    }`} />
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* Wallet TON */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="glass-panel border-white/10 rounded-3xl p-6"
                >
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <Icon name="account_balance_wallet" className="text-primary" />
                        {t('profile.walletTon')}
                    </h3>
                    <div className="text-center py-4">
                        {wallet?.account?.address ? (
                            /* Connected state */
                            <>
                                <span className="text-green-400 text-4xl mb-3 block">✅</span>
                                <p className="text-white font-mono text-sm mb-1">
                                    {truncateAddress(wallet.account.address)}
                                </p>
                                <p className="text-green-400/70 text-xs mb-4">{t('profile.connected')}</p>
                                <motion.button
                                    whileTap={{ scale: 0.95 }}
                                    onClick={handleDisconnectWallet}
                                    className="px-6 py-2 rounded-xl bg-red-600/20 border border-red-500/30 text-red-400 font-bold text-sm"
                                >
                                    {t('profile.disconnect')}
                                </motion.button>
                            </>
                        ) : (
                            /* Not connected state */
                            <>
                                <span className="text-white/30 text-4xl mb-3 block">🔗</span>
                                <p className="text-white/50 text-sm mb-2">{t('profile.connectWalletDesc')}</p>
                                <motion.button
                                    whileTap={{ scale: 0.95 }}
                                    onClick={handleConnectWallet}
                                    className="px-6 py-2 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-400 font-bold text-sm"
                                >
                                    {t('profile.connectWallet')}
                                </motion.button>
                            </>
                        )}
                    </div>
                </motion.div>

                {/* Transaction history (stub) */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="glass-panel border-white/10 rounded-3xl p-6"
                >
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <span className="text-lg">💳</span>
                        {t('profile.transactions')}
                    </h3>
                    <div className="text-center py-6">
                        <span className="text-white/20 text-3xl mb-2 block">📋</span>
                        <p className="text-white/40 text-sm">{t('profile.noTransactions')}</p>
                    </div>
                </motion.div>
            </main>

            {/* Bottom Navigation - Profile is accessed from home header, not from nav */}
            <BottomNav activeTab="profile" onTabChange={handleNavAction} />
        </div>
    );
}
